import { importWithRetry } from '@shared/core/chunkRetry';

// Counts what a person sees as one character (a grapheme): an emoji, a flag or an accented letter is 1,
// where `.length` counts 2 to 11 UTF-16 units. graphemer is ~93 KB minified, so it loads in its own chunk;
// until then a regex keeps the common clusters (flags, ZWJ emoji, skin tones, combining marks) together, which can
// differ by one on rarer scripts (Indic conjuncts, decomposed Hangul). Components that truncate call
// useCharacterCounter so the exact splitter is loaded before a person can act.
type SplitGraphemes = (text: string) => ReadonlyArray<string>;

let splitGraphemes: SplitGraphemes | null = null;

// A ZWJ joins emoji only; after a letter it stays on that letter, so `a\u200Db\u200Dc` counts 3, not 1.
const FALLBACK_CLUSTER =
  /\r\n|\p{Regional_Indicator}{2}|\p{Extended_Pictographic}(?:\p{M}|[\u{1F3FB}-\u{1F3FF}])*(?:\u200D\p{Extended_Pictographic}(?:\p{M}|[\u{1F3FB}-\u{1F3FF}])*)*[\u{E0020}-\u{E007F}]*\u200D?|[^\p{M}\p{Cc}](?:\p{M}|\u200D|[\u{1F3FB}-\u{1F3FF}])*|\p{Cc}|\p{M}+/gu;
const splitFallbackClusters = (text: string): ReadonlyArray<string> => text.match(FALLBACK_CLUSTER) ?? [];
let loading: Promise<void> | null = null;

export const loadCharacterCounter = (): Promise<void> => {
  if (!loading) {
    loading = importWithRetry(() => import(/* webpackChunkName: "graphemer" */ 'graphemer'), 'graphemer')
      .then(({ default: Graphemer }) => {
        const graphemer = new Graphemer();
        splitGraphemes = text => graphemer.splitGraphemes(text);
      })
      .catch(() => {
        // Keep the regex fallback and let the next caller retry.
        loading = null;
      });
  }
  return loading;
};

export const isCharacterCounterReady = (): boolean => splitGraphemes !== null;

const splitCharacters = (text: string): ReadonlyArray<string> => (splitGraphemes ? splitGraphemes(text) : splitFallbackClusters(text));

export const countCharacters = (text: string): number => splitCharacters(text).length;

/** Cuts to `max` characters without splitting an emoji or a surrogate pair. */
export const truncateCharacters = (text: string, max: number): string => {
  const characters = splitCharacters(text);
  return characters.length > max ? characters.slice(0, max).join('') : text;
};

/** Cuts to at most `maxLength` UTF-16 units, what a server-side cap counts, without splitting a character. */
export const truncateWithinLength = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  let truncated = '';
  for (const character of splitCharacters(text)) {
    if (truncated.length + character.length > maxLength) break;
    truncated += character;
  }
  return truncated;
};

export const isOverCharacterLimit = (text: string, max: number): boolean => countCharacters(text) > max;
